import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import GroupsIcon from '@mui/icons-material/Groups';
import LanguageIcon from '@mui/icons-material/Language';
import { Skeleton, Stack, Typography } from '@mui/material';
import { css } from '@emotion/react';
import { colors } from '../../../theme/colors';
import { ISourcingTrafficStat } from '../data/sourcingProfileMockData';
import LinkedinLogoIcon from '../../../assets/icons/linkedin_logo.svg?react';
import TwitterLogoIcon from '../../../assets/icons/twitter_logo.svg?react';

/**
 * Horizontal row of traction-metric cards (web traffic, followers, headcount, ...).
 * Rendered in the scrollable page body (not in the sticky header) so cards
 * scroll out of view as the user scrolls the profile.
 */

const statsRowCss = css`
  display: flex;
  gap: 8px;
  flex-wrap: nowrap;
  overflow-x: auto;
  padding: 0 12px 4px;
  width: 100%;
  box-sizing: border-box;
`;

const statCardCss = css`
  flex: 1 0 0;
  min-width: 120px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 6px 8px;
  border-radius: 4px;
  border: 2px solid white;
  background: radial-gradient(
    100% 204.54% at 0% 0%,
    rgba(249, 250, 250, 0.8) 0%,
    rgba(255, 255, 255, 0.4) 100%
  );
  box-shadow:
    0px -4px 40px rgba(255, 255, 255, 0.06),
    0px 4px 24px rgba(16, 37, 62, 0.04),
    0px 24px 48px rgba(75, 82, 93, 0.02);
  gap: 0;
`;

const statLabelRowCss = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
`;

const statLabelTextCss = css`
  font-size: 12px;
  line-height: 1.66;
  color: ${colors.text.main};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const statIconCircleCss = css`
  width: 20px;
  height: 20px;
  border-radius: 34px;
  background: ${colors.primary[10]};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
  color: ${colors.primary[60]};
`;

const statValueCss = css`
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  letter-spacing: 0.15px;
  color: ${colors.text.main};
  text-align: center;
`;

const statDeltaRowCss = css`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const dataChipPositiveCss = css`
  display: flex;
  align-items: center;
  height: 16px;
  border-radius: 4px;
  background: ${colors.tertiary[10]};
  padding: 0 4px 0 2px;
  flex-shrink: 0;
`;

const dataChipNegativeCss = css`
  display: flex;
  align-items: center;
  height: 16px;
  border-radius: 4px;
  background: ${colors.critical[10]};
  padding: 0 4px 0 2px;
  flex-shrink: 0;
`;

const dataChipTextPositiveCss = css`
  font-size: 10px;
  line-height: 1.64;
  letter-spacing: 0.4px;
  color: ${colors.tertiary[70]};
  white-space: nowrap;
`;

const dataChipTextNegativeCss = css`
  font-size: 10px;
  line-height: 1.64;
  letter-spacing: 0.4px;
  color: ${colors.critical[50]};
  white-space: nowrap;
`;

const statDeltaDescCss = css`
  font-size: 12px;
  line-height: 1.66;
  color: ${colors.text.secondary};
  white-space: nowrap;
`;

interface StatIconConfig {
  readonly icon: React.ReactNode;
  readonly hasCircleBg: boolean;
}

// Figma: the Language / Groups glyphs sit inside a light-blue circle and render
// in primary/60. LinkedIn / X glyphs already embed their brand color, so they
// skip the circular wrapper.
const statIconConfig: Record<string, StatIconConfig> = {
  'Monthly Web Traffic': { icon: <LanguageIcon sx={{ fontSize: 12 }} />, hasCircleBg: true },
  'Web Visitors Traffic': { icon: <LanguageIcon sx={{ fontSize: 12 }} />, hasCircleBg: true },
  'LinkedIn Followers': { icon: <LinkedinLogoIcon width={20} height={20} />, hasCircleBg: false },
  'X Followers': { icon: <TwitterLogoIcon width={20} height={20} />, hasCircleBg: false },
  Headcount: { icon: <GroupsIcon sx={{ fontSize: 12 }} />, hasCircleBg: true },
};

interface TrafficStatsRowProps {
  readonly stats: readonly ISourcingTrafficStat[];
}

export function TrafficStatsRow({ stats }: TrafficStatsRowProps) {
  if (stats.length === 0) {
    return null;
  }
  return (
    <div css={statsRowCss}>
      {stats.map((stat) => (
        <StatItem key={stat.label} stat={stat} />
      ))}
    </div>
  );
}

function StatItem({ stat }: { readonly stat: ISourcingTrafficStat }) {
  const iconCfg = statIconConfig[stat.label];
  const chipCss = stat.deltaPositive ? dataChipPositiveCss : dataChipNegativeCss;
  const chipTextCss = stat.deltaPositive ? dataChipTextPositiveCss : dataChipTextNegativeCss;
  const ArrowIcon = stat.deltaPositive ? ArrowDropUpIcon : ArrowDropDownIcon;
  const arrowColor = stat.deltaPositive ? colors.tertiary[70] : colors.critical[50];

  return (
    <div css={statCardCss}>
      <div css={statLabelRowCss}>
        {iconCfg &&
          (iconCfg.hasCircleBg ? (
            <div css={statIconCircleCss}>{iconCfg.icon}</div>
          ) : (
            <div style={{ width: 20, height: 20, flexShrink: 0, overflow: 'hidden', borderRadius: '100%' }}>
              {iconCfg.icon}
            </div>
          ))}
        <Typography css={statLabelTextCss}>{stat.label}</Typography>
      </div>
      <Typography css={statValueCss}>{stat.value}</Typography>
      <div css={statDeltaRowCss}>
        <div css={chipCss}>
          <ArrowIcon sx={{ fontSize: 16, color: arrowColor }} />
          <Typography css={chipTextCss}>{stat.delta}</Typography>
        </div>
        <Typography css={statDeltaDescCss}>{stat.deltaDescription}</Typography>
      </div>
    </div>
  );
}

const SKELETON_PLACEHOLDERS = ['traffic', 'linkedin', 'twitter', 'headcount'] as const;

export function TrafficStatsRowSkeleton() {
  return (
    <Stack direction='row' gap={1} sx={{ px: '12px', pb: '4px', width: '100%' }}>
      {SKELETON_PLACEHOLDERS.map((id) => (
        <Skeleton key={id} variant='rounded' height={76} sx={{ flex: 1, borderRadius: '4px' }} />
      ))}
    </Stack>
  );
}
